import _ from 'lodash'
import type { Ref, Update, Transform, Materializer } from '@wix/materializer'
import { createRef } from '@wix/materializer'
import type { DataMapName, Component, WidgetRefDataItem } from '@wix/thunderbolt-becky-types'
import {
	Batched,
	UpdateData,
	UpdateStructure,
	UpdateEnvironment,
	removeHash,
	createRefResolver,
} from '@wix/thunderbolt-catharsis'
import type {
	ByRefsisOps,
	ByRefsisTransactionWithInvalidations,
	RawModel,
	WithNamespace,
	UpdateTranslation,
} from '../types'
import {
	getRefCreatorInNamespace,
	isLayoutOrStyleLeafOverride,
	getContextCompIdFromOverrideDataId,
	getLeafsOverridesMapAspectName,
	getQueryNameFromLeafsOverridesMapAspectName,
	getRefTemplateKey,
	isRefComponent,
} from './byRefUtils'
import {
	SCHEMAS,
	EMPTY_REMOVED,
	SITE_NAMESPACE,
	EMPTY_SCHEMA,
	REF_COMPONENTS_TEMPLATE_KEY_ASPECT,
	DATA_MAP_NAMES,
	DATA_MAP_TO_QUERY,
	BY_REFSIS_DELIMITER,
	QUERY_MAPPING,
	DATA_QUERY_TO_COMP_ID_ASPECT,
	ENVIRONMENT_NAMESPACE,
	RAW_TRANSLATIONS_ASPECT,
	RESOLVED_TRANSLATIONS_ASPECT,
	ORIGINAL_LANGUAGE_REF,
	LANGUAGE_REF,
} from './constants'
import { ByRefsisCompNode, createByRefsisCompNode, ScopedCreateRef } from './compNodeUtils'
import { byRefsisDataNodes } from './refConsumption/dataNodes'
import { translate } from './translations'

const DATA_MAPS_TO_RESOLVE = new Set<DataMapName>(['presets', 'layout_data', 'theme_data'])

const modelUpdateCompNodes: Record<string, ByRefsisCompNode<any, any>> = {
	[REF_COMPONENTS_TEMPLATE_KEY_ASPECT]: createByRefsisCompNode({
		getDependencies: (component: Component, scopedCreateRef?: ScopedCreateRef) =>
			isRefComponent(component)
				? (scopedCreateRef!('document_data', removeHash(component.dataQuery!)) as Ref<WidgetRefDataItem>)
				: undefined,
		toViewItem: (__, refCompData) => {
			if (!refCompData) {
				return null
			}

			return getRefTemplateKey(refCompData)
		},
	}),
}

const defaultDataNodeToViewItem = (rawItem: any, refs: any, dependencies: any) => {
	const result = { ...rawItem, ...refs, ...(dependencies && { dependencies }) }

	delete result.metaData
	return result
}

export const getModelUpdateTransformer = (rawModel: RawModel): Transform => (value, path) => {
	const [namespace, aspect, id] = path

	if (
		namespace === ENVIRONMENT_NAMESPACE ||
		aspect.endsWith('LeafsOverridesMap') ||
		aspect === DATA_QUERY_TO_COMP_ID_ASPECT
	) {
		return value
	}

	if (aspect === RESOLVED_TRANSLATIONS_ASPECT) {
		const { originalLanguage, language } = value
		// in carmi we also check if language is undefined, don't know if it's possible.
		// reference: https://github.com/wix-private/thunderbolt/blob/4a7ac007c177bd52667a3a8e4af2d98eda252fb2/packages/thunderbolt-becky/src/carmi/data.carmi.ts#L18
		if (language === originalLanguage) {
			return
		}

		return rawModel[namespace][RAW_TRANSLATIONS_ASPECT][language]?.[id]
	}

	if (DATA_MAP_NAMES.has(aspect as DataMapName)) {
		const rawItem = rawModel[namespace][aspect][id]
		const { schema, custom } = value || {}

		if (!schema && !custom) {
			return rawItem
		}

		if (custom?.translation) {
			return translate(rawItem, custom.translation)
		}

		if (typeof rawItem === 'undefined') {
			return rawItem
		}

		return defaultDataNodeToViewItem(rawItem, schema, custom)
	}

	// namespace is either 'site' or a templateKey
	const component = rawModel[namespace].structure[id]
	return component && modelUpdateCompNodes[aspect].toViewItem(component, value)
}

const getRefsForDataItem = createRefResolver(SCHEMAS)

export const updateModel: ByRefsisTransactionWithInvalidations = (
	materializer: Materializer,
	rawModel: RawModel,
	modelUpdateFn: (ops: ByRefsisOps) => void
) => {
	const leafsOverridesToUpdate = new Map<string, Record<string, true>>()

	const updateDataItemIdToCompId = (
		batchUpdate: Update<void>,
		namespace: string,
		component: Component | undefined
	) => {
		if (!component?.dataQuery) {
			return
		}
		const { dataQuery } = component
		batchUpdate(namespace, DATA_QUERY_TO_COMP_ID_ASPECT, removeHash(dataQuery), component.id, EMPTY_SCHEMA)
	}

	const updateDataByNamespace: Batched<WithNamespace<UpdateData>> = (batchUpdate) => (
		namespace,
		dataMapName,
		id,
		dataItem
	) => {
		_.set(rawModel, [namespace, dataMapName, id], dataItem)
		if (!dataItem) {
			batchUpdate(namespace, dataMapName, id)
			return
		}

		const createRefInTemplateNamespace = getRefCreatorInNamespace(namespace)
		const customDeps = byRefsisDataNodes[dataMapName]?.getDependencies(dataItem, createRefInTemplateNamespace)
		const schemaRefs = DATA_MAPS_TO_RESOLVE.has(dataMapName)
			? getRefsForDataItem(dataItem, dataMapName, namespace)
			: null
		const dependencies = schemaRefs || customDeps ? { schema: schemaRefs, custom: customDeps } : null
		batchUpdate(namespace, dataMapName, id, dependencies, { schema: dependencies, removed: EMPTY_REMOVED })

		const queryName = DATA_MAP_TO_QUERY[dataMapName]

		if (isLayoutOrStyleLeafOverride(queryName, id)) {
			const { compId, context } = getContextCompIdFromOverrideDataId(id, queryName)
			const overridesAspect = getLeafsOverridesMapAspectName(context, queryName)
			const overridesMapPath = [namespace, overridesAspect, compId]
			const overridesMapStringPath = overridesMapPath.join(BY_REFSIS_DELIMITER)

			if (!leafsOverridesToUpdate.has(overridesMapStringPath)) {
				const overridesMap = { ..._.get(rawModel, overridesMapPath) }
				leafsOverridesToUpdate.set(overridesMapStringPath, overridesMap)
			}

			leafsOverridesToUpdate.get(overridesMapStringPath)![id] = true
		}
	}

	const updateData: Batched<UpdateData> = (batchUpdate) => (dataMapName, id, dataItem) =>
		updateDataByNamespace(batchUpdate)(SITE_NAMESPACE, dataMapName, id, dataItem)

	const updateTranslationByNamespace: Batched<WithNamespace<UpdateTranslation>> = (batchUpdate) => (
		namespace,
		language,
		documentDataId,
		documentDataItem
	) => {
		_.set(rawModel, [namespace, RAW_TRANSLATIONS_ASPECT, language, documentDataId], documentDataItem)

		if (!documentDataItem) {
			batchUpdate(namespace, RESOLVED_TRANSLATIONS_ASPECT, documentDataId)
			return
		}

		const dependencies = {
			originalLanguage: ORIGINAL_LANGUAGE_REF,
			language: LANGUAGE_REF,
		}

		batchUpdate(namespace, RESOLVED_TRANSLATIONS_ASPECT, documentDataId, dependencies, {
			schema: dependencies,
			removed: EMPTY_REMOVED,
		})
	}

	const updateTranslation: Batched<UpdateTranslation> = (batchUpdate) => (language, id, dataItem) =>
		updateTranslationByNamespace(batchUpdate)(SITE_NAMESPACE, language, id, dataItem)

	const updateStructureByNamespace: Batched<WithNamespace<UpdateStructure>> = (batchUpdate) => (
		namespace,
		id,
		component
	) => {
		_.set(rawModel, [namespace, 'structure', id], component)
		updateDataItemIdToCompId(batchUpdate, namespace, component)

		for (const structureCalculator in modelUpdateCompNodes) {
			const value =
				component &&
				modelUpdateCompNodes[structureCalculator].getDependencies(
					component,
					getRefCreatorInNamespace(namespace)
				)
			batchUpdate(namespace, structureCalculator, id, value, {
				schema: value,
				removed: EMPTY_REMOVED,
			})
		}
	}

	const updateWidgetRootCompId = (templateKey: string, rootCompId: string) => {
		_.set(rawModel, [templateKey, 'rootCompId'], rootCompId)
	}

	const updateStructure: Batched<UpdateStructure> = (batchUpdate) => (id, component) =>
		updateStructureByNamespace(batchUpdate)(SITE_NAMESPACE, id, component)

	const updateEnvironment: Batched<UpdateEnvironment> = (batchUpdate) => (environment) => {
		for (const envType in environment) {
			for (const key in environment[envType]) {
				batchUpdate(ENVIRONMENT_NAMESPACE, envType, key, environment[envType][key], EMPTY_SCHEMA)
			}
		}
	}

	const flushLeafsOverridesMapsUpdates = (batchUpdate: Update<void>) => {
		for (const [overridesMapStringPath, overridesMap] of leafsOverridesToUpdate) {
			const overridesMapPath = overridesMapStringPath.split(BY_REFSIS_DELIMITER)
			const [overridesNamespace, overridesAspect, overridesCompId] = overridesMapPath
			const queryName = getQueryNameFromLeafsOverridesMapAspectName(overridesMapPath[1])
			_.set(rawModel, overridesMapPath, overridesMap)

			const overrideRefsMap = _.mapValues(overridesMap, (__, overrideId) =>
				createRef([overridesNamespace, QUERY_MAPPING[queryName], overrideId])
			)
			batchUpdate(overridesNamespace, overridesAspect, overridesCompId, overrideRefsMap, {
				schema: overrideRefsMap,
				removed: [],
			})
		}

		leafsOverridesToUpdate.clear()
	}

	const invalidations = materializer.batch((batchUpdate) => {
		modelUpdateFn({
			updateData: updateData(batchUpdate),
			updateStructure: updateStructure(batchUpdate),
			updateTranslation: updateTranslation(batchUpdate),
			updateWidgetRootCompId,
			updateWidgetData: updateDataByNamespace(batchUpdate),
			updateWidgetStructure: updateStructureByNamespace(batchUpdate),
			updateWidgetTranslation: updateTranslationByNamespace(batchUpdate),
			updateEnvironment: updateEnvironment(batchUpdate),
		})

		flushLeafsOverridesMapsUpdates(batchUpdate)
	}) as Array<Array<string>>

	return invalidations
}
